import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar, CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';
import type { ApproveWorkAuth } from '@shared/schemas/workAuth';
import { useToast } from '@/hooks/useToast';

interface WorkAuthApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ApproveWorkAuth) => Promise<void>;
  workAuthId: string;
  employeeName: string;
}

const WorkAuthApprovalModal: React.FC<WorkAuthApprovalModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  workAuthId,
  employeeName,
}) => {
  const { toast } = useToast();
  const [isPermanent, setIsPermanent] = useState(false);
  const [permanentConfirmed, setPermanentConfirmed] = useState(false);
  const [newExpirationDate, setNewExpirationDate] = useState('');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    // Validation
    if (!isPermanent && !newExpirationDate) {
      toast.error('Validation Error', 'Please provide an expiration date for temporary approval');
      return;
    }

    if (isPermanent && !permanentConfirmed) {
      toast.error('Confirmation Required', 'Please confirm that you understand this employee will be removed from work authorization tracking');
      return;
    }

    setIsLoading(true);
    try {
      await onSubmit({
        isPermanent,
        newExpirationDate: newExpirationDate ? new Date(newExpirationDate) : undefined,
        notes: notes || undefined,
      });

      // Success - close modal and reset form
      toast.success('Work Authorization Approved');
      setIsPermanent(false);
      setPermanentConfirmed(false);
      setNewExpirationDate('');
      setNotes('');
      onClose();
    } catch (err) {
      let errorMessage = 'Failed to approve work authorization';

      if (typeof err === 'string') {
        errorMessage = err;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      } else if (typeof err === 'object' && err !== null && 'message' in err) {
        errorMessage = (err as any).message;
      }

      toast.error('Approval Failed', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setIsPermanent(false);
      setPermanentConfirmed(false);
      setNewExpirationDate('');
      setNotes('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Approve Work Authorization</DialogTitle>
          <DialogDescription>
            Set approval terms for {employeeName}'s work authorization
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Approval Type */}
          <div className="space-y-2">
            <Label>Approval Type</Label>
            <Select value={isPermanent ? 'PERMANENT' : 'TEMPORARY'} onValueChange={(val) => {
              setIsPermanent(val === 'PERMANENT');
              if (val !== 'PERMANENT') {
                setPermanentConfirmed(false);
              }
            }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TEMPORARY">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Temporary (Renewal Required)
                  </div>
                </SelectItem>
                <SelectItem value="PERMANENT">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Permanent (No Renewal)
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Temporary Approval */}
          {!isPermanent && (
            <div className="space-y-2">
              <Label htmlFor="expirationDate" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Expiration Date <span className="text-red-600">*</span>
              </Label>
              <Input
                id="expirationDate"
                type="date"
                value={newExpirationDate}
                onChange={(e) => setNewExpirationDate(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">
                Set the date when this work authorization expires and renewal will be required
              </p>
            </div>
          )}

          {/* Permanent Approval Confirmation */}
          {isPermanent && (
            <div className="space-y-3">
              <Alert variant="destructive" className="border-amber-500 bg-amber-50 text-amber-900">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  <strong>Important:</strong> Marking this as permanent will remove {employeeName} from work authorization tracking. They will no longer receive renewal notifications or appear in expiration reports.
                </AlertDescription>
              </Alert>

              <div className="flex items-start space-x-3 p-3 border rounded-md bg-muted/50">
                <Checkbox
                  id="permanentConfirm"
                  checked={permanentConfirmed}
                  onCheckedChange={(checked: boolean | 'indeterminate') => setPermanentConfirmed(checked === true)}
                />
                <label
                  htmlFor="permanentConfirm"
                  className="text-sm font-medium leading-tight cursor-pointer"
                >
                  I understand that this employee will be removed from further work authorization tracking and will not receive renewal reminders.
                </label>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              placeholder="Add any notes about this approval..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Approving...
              </>
            ) : (
              <>Approve Work Authorization</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkAuthApprovalModal;
